import { addresses } from "@rally/agreed";
import type { Happening, Who } from "./choosing.ts";

export type Row = {
  kind: string;
  id: string;
  conversation: string;
  at: string | null;
  said?: string | null;
  state?: string;
  unread?: number;
  muted?: boolean;
};

export type Reader = { handle: string };

function unreadIn(rows: readonly Row[]): Map<string, { unread: number; muted: boolean }> {
  const found = new Map<string, { unread: number; muted: boolean }>();
  for (const row of rows) found.set(row.conversation, { unread: row.unread ?? 0, muted: row.muted === true });
  return found;
}

function voiceIn(rows: readonly Row[], conversation: string): Who {
  const running = rows.some((row) => row.kind === "run" && row.conversation === conversation);
  return running ? "agent" : "human";
}

function saidIn(rows: readonly Row[], conversation: string): string {
  const row = rows.find((one) => one.kind === "message" && one.conversation === conversation);
  return row?.said ?? "";
}

function runsIn(rows: readonly Row[]): Map<string, string> {
  const found = new Map<string, string>();
  for (const row of rows) if (row.kind === "run") found.set(row.id, row.state ?? "");
  return found;
}

function roomsOfRuns(rows: readonly Row[]): Map<string, string> {
  const found = new Map<string, string>();
  for (const row of rows) if (row.kind === "run") found.set(row.id, row.conversation);
  return found;
}

export function whatArrived(before: readonly Row[], after: readonly Row[], reader: Reader): Happening[] {
  if (before.length === 0) return [];
  const was = unreadIn(before);
  const now = unreadIn(after);
  const happenings: Happening[] = [];

  for (const [conversation, count] of now) {
    if (count.muted) continue;
    const grew = count.unread > (was.get(conversation)?.unread ?? 0);
    if (!grew) continue;
    const said = saidIn(after, conversation);
    happenings.push({
      what: "said",
      conversation,
      by: voiceIn(after, conversation),
      addressed: addresses(said, reader.handle),

      mine: false,
    });
  }

  const running = runsIn(before);
  const still = runsIn(after);
  const rooms = roomsOfRuns([...before, ...after]);
  for (const [run, state] of still) {
    if (state !== "waiting" || running.get(run) === "waiting") continue;
    const conversation = rooms.get(run);
    if (conversation) happenings.push({ what: "turn waiting", conversation });
  }
  for (const [run] of running) {
    if (still.has(run)) continue;
    const conversation = rooms.get(run);
    if (conversation) happenings.push({ what: "turn ended", conversation });
  }

  return happenings;
}
